/**
 * Сетка фото папки: ровные плитки 3:4, превью 800 px (`thumb_path`) через
 * `RetryImg` — общая очередь загрузки и повтор при отказе шлюза, как у пинов.
 * Превью нет (HEIC не сконвертировался) — заглушка с причиной из `error`.
 *
 * Клики — как в `PinTile`: по картинке — просмотр, Ctrl/Cmd — выделить,
 * Shift — диапазон, в режиме «выделять» обычный клик = выделить. Чекбокс —
 * отдельная кнопка-сосед, не внутри кнопки картинки. Набор выделенных держит
 * экран папки, сетка сообщает только индекс.
 *
 * Бейджи: «закреплено» — папку поставил человек (`manual`), кластеризация фото
 * не сдвинет; «обложка» — это фото на карточке папки.
 */
import { Check, ImageOff, Pin, Star } from "lucide-react";

import { RetryImg } from "@/components/trends/PinGrid";
import { mediaUrl, when } from "@/lib/media";
import { surname, type Photo } from "@/lib/photos";

export function PhotoGrid({
  photos,
  selected,
  selecting,
  onToggle,
  onOpen,
  coverId,
}: {
  photos: Photo[];
  selected: ReadonlySet<number>;
  selecting: boolean;
  /** Индекс — в показанном порядке; `range` — Shift: диапазон от прошлого выделенного. */
  onToggle: (index: number, range: boolean) => void;
  onOpen: (index: number) => void;
  /** Фото на обложке папки; `null` — обложка по умолчанию (первое по дате). */
  coverId: number | null;
}) {
  return (
    <div className="grid grid-cols-[repeat(auto-fill,minmax(160px,1fr))] gap-3" data-annot="сетка фото">
      {photos.map((photo, i) => (
        <PhotoTile
          key={photo.id}
          photo={photo}
          index={i}
          isCover={photo.id === coverId}
          isSelected={selected.has(photo.id)}
          boxVisible={selecting || selected.size > 0}
          selecting={selecting}
          onToggle={onToggle}
          onOpen={() => onOpen(i)}
        />
      ))}
    </div>
  );
}

function PhotoTile({
  photo,
  index,
  isCover,
  isSelected,
  boxVisible,
  selecting,
  onToggle,
  onOpen,
}: {
  photo: Photo;
  index: number;
  isCover: boolean;
  isSelected: boolean;
  boxVisible: boolean;
  selecting: boolean;
  onToggle: (index: number, range: boolean) => void;
  onOpen: () => void;
}) {
  const first = photo.mentions[0];
  const fileName = first?.file_name ?? "фото";

  return (
    <figure className="min-w-0">
      <div className="group relative">
        <button
          type="button"
          onClick={(e) => (selecting || e.ctrlKey || e.metaKey || e.shiftKey ? onToggle(index, e.shiftKey) : onOpen())}
          // Shift-клик иначе выделяет текст страницы от прошлого клика до этого.
          onMouseDown={(e) => (e.shiftKey ? e.preventDefault() : undefined)}
          className={`relative block w-full overflow-hidden rounded-[var(--radius-card)] border border-border bg-bg-subtle text-left focus-visible:outline-2 focus-visible:outline-accent ${
            isSelected ? "outline-2 outline-offset-[-2px] outline-accent" : ""
          }`}
          style={{ aspectRatio: "3 / 4" }}
          title={fileName}
        >
          {photo.thumb_path ? (
            <RetryImg
              src={mediaUrl(photo.thumb_path)}
              alt={fileName}
              className="absolute inset-0 h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
            />
          ) : (
            <span className="absolute inset-0 grid place-items-center p-2 text-center text-[12px] text-fg-faint">
              <span className="grid justify-items-center gap-1">
                <ImageOff size={18} />
                {photo.error ?? "превью нет"}
              </span>
            </span>
          )}

          <span className="absolute top-1.5 left-1.5 grid justify-items-start gap-1">
            {photo.link_kind === "manual" ? (
              <span
                className="inline-flex items-center gap-1 rounded-[4px] bg-black/60 px-1.5 py-0.5 text-[11px] font-medium text-white"
                title="Папку поставил человек — кластеризация это фото не сдвинет"
              >
                <Pin size={10} />
                закреплено
              </span>
            ) : null}
            {isCover ? (
              <span
                className="inline-flex items-center gap-1 rounded-[4px] bg-accent px-1.5 py-0.5 text-[11px] font-medium text-bg"
                title="Это фото — на карточке папки"
              >
                <Star size={10} />
                обложка
              </span>
            ) : null}
          </span>
        </button>
        <button
          type="button"
          aria-pressed={isSelected}
          aria-label={isSelected ? "Снять выделение" : "Выделить"}
          title="Выделить (Shift — диапазон)"
          onMouseDown={(e) => (e.shiftKey ? e.preventDefault() : undefined)}
          onClick={(e) => {
            e.stopPropagation();
            onToggle(index, e.shiftKey);
          }}
          className={`absolute top-1.5 right-1.5 grid size-5 place-items-center rounded-[4px] border transition-opacity ${
            isSelected ? "border-accent bg-accent text-bg" : "border-white/80 bg-black/45 text-white hover:bg-black/65"
          } ${isSelected || boxVisible ? "opacity-100" : "opacity-0 group-hover:opacity-100 focus-visible:opacity-100"}`}
        >
          {isSelected ? <Check size={13} strokeWidth={3} /> : null}
        </button>
      </div>
      <figcaption className="mt-1 flex min-w-0 gap-1.5 overflow-hidden px-0.5 text-[11.5px] whitespace-nowrap text-fg-faint">
        {first ? (
          <>
            <span className="min-w-0 truncate text-fg-muted" title={first.user_name}>
              {surname(first.user_name)}
            </span>
            {first.location_short ? <span className="flex-none">· {first.location_short}</span> : null}
            <span className="flex-none tnum">· {when(first.date)}</span>
            {photo.mentions.length > 1 ? (
              <span className="flex-none" title="Сколько раз это фото присылали в чат">
                · ×{photo.mentions.length}
              </span>
            ) : null}
          </>
        ) : photo.crm_media_id ? (
          <span className="tnum">из CRM · медиа {photo.crm_media_id}</span>
        ) : (
          <span>без сообщения</span>
        )}
      </figcaption>
    </figure>
  );
}
